/**
 * DeleteAccountModal Component
 * 
 * Modal for confirming account deletion.
 * 
 * Features:
 * - Modal overlay with focus trap
 * - Case-sensitive DELETE confirmation
 * - Keyboard navigation support
 * - WCAG 2.2 AA compliance
 * 
 * @see .specify/memory/constitution.md
 * @see specs/004-settings-profile-png/spec.md
 */

import React, { useEffect, useRef, useState } from 'react';
import { X, AlertTriangle } from 'lucide-react';
import { DeleteAccountInput } from '../../../types/profile.types';

interface DeleteAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: DeleteAccountInput) => void;
  loading?: boolean;
}

const REQUIRED_CONFIRMATION = 'DELETE';

export function DeleteAccountModal({ 
  isOpen, 
  onClose, 
  onSubmit, 
  loading = false
}: DeleteAccountModalProps): React.ReactElement | null {
  const modalRef = useRef<HTMLDivElement>(null);
  const firstFocusableRef = useRef<HTMLButtonElement>(null);
  const confirmationInputRef = useRef<HTMLInputElement>(null);
  const [confirmationText, setConfirmationText] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setConfirmationText('');
      setErrors({});
    }
  }, [isOpen]);

  // Focus trap and keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    
    // Focus the confirmation input
    if (confirmationInputRef.current) {
      confirmationInputRef.current.focus();
    }

    // Prevent body scroll
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setConfirmationText(e.target.value);
    // Clear error
    if (errors.confirmation) {
      setErrors(prev => {
        const next = { ...prev };
        delete next.confirmation;
        return next;
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (confirmationText !== REQUIRED_CONFIRMATION) {
      newErrors.confirmation = `Please type "${REQUIRED_CONFIRMATION}" to confirm`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    onSubmit({ confirmation: confirmationText as 'DELETE' });
  };

  const isConfirmButtonDisabled = loading || confirmationText !== REQUIRED_CONFIRMATION;

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby="delete-account-title"
      aria-describedby="delete-account-description"
    >
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={handleBackdropClick}
      />
      
      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          ref={modalRef}
          className="relative w-full max-w-md transform overflow-hidden rounded-lg bg-white shadow-xl transition-all dark:bg-gray-900"
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
            <div>
              <h2 id="delete-account-title" className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Delete Account
              </h2>
              <p id="delete-account-description" className="text-sm text-gray-600 dark:text-gray-400">
                Permanently delete your account and all data
              </p>
            </div>
            <button
              ref={firstFocusableRef}
              onClick={onClose}
              className="btn-ghost btn-sm rounded-md p-2"
              aria-label="Close modal"
              disabled={loading}
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <form onSubmit={handleSubmit} className="px-6 py-4">
            <div className="space-y-4">
              {/* Warning Message */}
              <div className="rounded-lg bg-red-50 p-4 dark:bg-red-900/20">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" aria-hidden="true" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-red-800 dark:text-red-200 mb-2">
                      This will permanently delete your account and all associated data including:
                    </p>
                    <ul className="list-disc list-inside text-sm text-red-700 dark:text-red-300 space-y-1 mb-3">
                      <li>All transactions and financial records</li>
                      <li>All categories and tags</li>
                      <li>All account settings and preferences</li>
                      <li>Your profile information</li>
                    </ul>
                    <p className="text-sm text-red-800 dark:text-red-200 font-medium">
                      Your account will be marked for deletion immediately, but you have a 30-day recovery window to restore it before permanent deletion.
                    </p>
                  </div>
                </div>
              </div>

              {/* Confirmation Input */}
              <div>
                <label 
                  htmlFor="delete-confirmation" 
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                >
                  Type <span className="font-bold text-red-600 dark:text-red-400">{REQUIRED_CONFIRMATION}</span> to confirm
                </label>
                <input
                  ref={confirmationInputRef}
                  type="text"
                  id="delete-confirmation"
                  value={confirmationText}
                  onChange={handleChange}
                  className={`input w-full ${errors.confirmation ? 'border-red-500' : ''}`}
                  placeholder={REQUIRED_CONFIRMATION}
                  required
                  aria-invalid={!!errors.confirmation}
                  aria-describedby={errors.confirmation ? 'delete-confirmation-error' : undefined}
                  autoComplete="off"
                />
                {errors.confirmation && (
                  <p id="delete-confirmation-error" className="mt-1 text-sm text-red-600 dark:text-red-400">
                    {errors.confirmation}
                  </p>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="mt-6 flex justify-end space-x-3">
              <button 
                type="button" 
                onClick={onClose} 
                className="btn-secondary btn-md"
                disabled={loading}
              >
                Cancel
              </button>
              <button 
                type="submit" 
                className="btn-destructive btn-md"
                disabled={isConfirmButtonDisabled}
              >
                {loading ? 'Deleting...' : 'Delete Account'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

